// src/components/crm/QuoteActivityRow.js
// SEAM 3 VERIFIER: qact-3b7e9d52-row   (extracted in Seam 6, qfup-7d3e9a41)
//
// ONE row of a quote's activity log, plus the describe() that turns a stored
// entry into its human sentence. Extracted VERBATIM out of
// QuoteActivityTimeline.js so two surfaces can render the same row without a
// second copy of the labels:
//   * QuoteActivityTimeline — every type, interleaved (Seam 3).
//   * QuoteFollowUpStrip    — follow-ups only (Seam 6).
//
// Every type in QUOTE_ACTIVITY_TYPES gets a label, including types no code
// writes yet, and anything unrecognised falls back to a generic label — so a
// later seam adding entries can't break either surface.
//
// note_added rows: note CONTENT lives in Notes & Media, so a row only says a
// note was added and, when clicked, opens the Notes & Media strip. A noteId the
// page's loaded notes don't contain reads "Note (deleted)" with no link.
import React from 'react';
import { toJsDate } from '../../utils/formatters';
import { QUOTE_ACTIVITY_TYPES as T, findPipelineStage } from '../../utils/quoteSchema';

export const humanize = (s) => String(s || '').replace(/_/g, ' ');

// qsnooze-9f4c2e18 — hasTime DEFAULTS TO TRUE so every existing caller keeps its
// behaviour: an `at` stamp is a real instant and should show its time. Pass
// false for a DATE-ONLY intention, which is stored as midday local and would
// otherwise read as a 12:00 PM appointment nobody agreed to.
//
// 🛑 CALLERS MUST COERCE THE FLAG — pass `x === true`, never the raw field.
// A default parameter fires on `undefined`, and `undefined` is exactly what an
// activity row written before this flag existed gives you, so passing the raw
// value defaults it back to TRUE and fabricates a noon appointment. That was a
// real bug (found on E-0007, 2026-09-27): a date-only follow-up displayed as
// "Oct 3, 2026, 12:00 PM". An earlier version of this comment reasoned that
// `null !== undefined` and concluded the default was safe — correct about
// `null`, and wrong about the case that actually occurs.
export const formatWhen = (value, hasTime = true) => {
    const d = toJsDate(value);
    if (!d) return '';
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        ...(hasTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
};

// qpipe-4d1a7c35-timeline — stage_change entries store stage IDS (so renames
// never rewrite history); the label is resolved here from the org's current
// stage list. A stage that has since been deleted reads "a removed stage".
function stageName(id, stages) {
    if (!id) return null;
    if (!stages) return 'a stage';
    const s = findPipelineStage(id, stages);
    return s ? `"${s.label}"` : 'a removed stage';
}

const STAGE_REASON_SUBLABEL = {
    stage_deleted: 'stage deleted',
    converted_to_job: 'converted to a job',
    job_deleted: 'job deleted',
    viewed: 'the customer opened it', // qview-6b2d4e91 — the Seam 10 Sent → Viewed move
};

// qclose-3e8a5c17 — why a close happened, from the 'closed' entry. Manual moves
// have no cause beyond the person (shown as "by …"); automatic ones carry the
// move's own reason: 'status:Sent→Approved', 'converted_to_job', 'stage_deleted'.
export function closeCauseLabel(d) {
    if (!d || d.trigger !== 'auto' || !d.autoReason) return null;
    if (STAGE_REASON_SUBLABEL[d.autoReason]) return STAGE_REASON_SUBLABEL[d.autoReason];
    const m = /^status:.*→(.+)$/.exec(d.autoReason);
    return m ? `status changed to ${m[1]}` : null;
}

// qclose-3e8a5c17 — a close writes TWO entries in one commit: the stage_change
// (time-in-stage history) and the 'closed' event. Shown together they read as
// the same thing twice (roadmap D35), so the timeline shows the closed row and
// hides its partner: the stage_change moving to the SAME stage within a few
// seconds of it (one commit → one server timestamp; the margin only absorbs a
// pending local stamp). Returns the Set of stage_change ids to hide.
export function pairCloseRows(entries) {
    const hide = new Set();
    const ms = (v) => { const dt = toJsDate(v); return dt ? dt.getTime() : null; };
    const closes = (entries || []).filter((e) => e.type === T.CLOSED);
    for (const c of closes) {
        const at = ms(c.at);
        const partner = (entries || []).find((e) => e.type === T.STAGE_CHANGE
            && !hide.has(e.id)
            && e.data?.to === c.data?.toStageId
            && at !== null && ms(e.at) !== null && Math.abs(ms(e.at) - at) < 5000);
        if (partner) hide.add(partner.id);
    }
    return hide;
}

// Returns { label, sublabel? } for one entry. docWord comes from THIS quote's
// docType (terminology freeze rule: per-document surfaces).
export function describe(entry, docWord, noteState, stages) {
    const d = entry.data || {};
    switch (entry.type) {
        case T.SENT:
            return d.channel === 'email'
                ? { label: `${docWord} sent by email${d.recipient ? ` to ${d.recipient}` : ''}` }
                : { label: 'Marked as sent (no email from Spatchy)' };
        case T.NOTE_ADDED: {
            if (noteState === 'deleted') return { label: 'Note (deleted)' };
            const label = d.hasMedia
                ? `Photo added${d.mediaCount > 1 ? ` (${d.mediaCount})` : ''}`
                : 'Note added';
            return { label, sublabel: d.visibility === 'customer' ? 'customer-visible' : 'internal' };
        }
        case T.STAGE_CHANGE: {
            const to = stageName(d.to, stages) || 'a stage';
            const from = stageName(d.from, stages);
            const reason = STAGE_REASON_SUBLABEL[d.reason] || (typeof d.reason === 'string' && d.reason.startsWith('status:') ? 'status changed' : undefined);
            return {
                label: from ? `Stage changed from ${from} to ${to}` : `Placed in ${to}`,
                sublabel: reason,
            };
        }
        // qfup-7d3e9a41 — Seam 6. method/result are the ORG'S OWN WORDS, stored
        // verbatim (Stacey's defaults, or whatever an org renamed them to).
        // humanize only swaps underscores for spaces, so a phrase like
        // "Spoke with client" passes through untouched. Never re-map these to a
        // display table: the row must keep saying what was true when it was
        // written, even after the org edits its vocabulary.
        case T.FOLLOW_UP: {
            const label = `Follow-up logged: ${humanize(d.method)}${d.result ? `, ${humanize(d.result)}` : ''}`;
            // qsnooze-9f4c2e18 — show a time ONLY if one was actually chosen.
            // `=== true` is load-bearing: rows written before the flag existed
            // have the key ABSENT (undefined), which would trigger formatWhen's
            // `= true` default and invent a noon appointment. Absent means
            // date-only — every pre-flag follow-up used Seam 6's midday proxy.
            const next = formatWhen(d.nextFollowUpAt, d.nextFollowUpHasTime === true);
            return next ? { label, sublabel: `next follow-up ${next}` } : { label };
        }
        case T.REMINDER_SENT:
            return { label: `Follow-up reminder sent to the team${d.dayThreshold ? ` (day ${d.dayThreshold})` : ''}` };
        case T.VIEWED:
            return { label: `Customer viewed the ${docWord.toLowerCase()}` };
        case T.SNOOZED:
            return { label: `Snoozed${d.until ? ` until ${formatWhen(d.until, d.untilHasTime === true)}` : ''}` };
        case T.UNSNOOZED:
            return { label: d.reason === 'cleared' ? 'Snooze cleared' : 'Snooze ended' };
        case T.EXPIRY_WARNED:
            return { label: `Expiry warning${typeof d.daysLeft === 'number' ? ` (${d.daysLeft} day${d.daysLeft === 1 ? '' : 's'} left)` : ''}` };
        // qclose-3e8a5c17 — Seam 9b: ONE row per close. The timeline hides the
        // stage_change written in the same commit (pairCloseRows), so this row
        // carries the move itself: where it died, and why it moved.
        case T.CLOSED: {
            const outcome = humanize(d.outcome) || 'closed';
            const from = stageName(d.fromStageId, stages);
            const label = d.previousOutcome
                ? `Changed from ${humanize(d.previousOutcome)} to ${outcome}`
                : `Closed as ${outcome}${from ? ` from ${from}` : ''}`;
            const sublabel = closeCauseLabel(d);
            return sublabel ? { label, sublabel } : { label };
        }
        case T.CUSTOMER_RESPONDED: {
            if (d.response === 'approved') {
                return { label: `Customer approved the ${docWord.toLowerCase()}${d.awaitingDeposit ? ' (deposit pending)' : ''}` };
            }
            if (d.response === 'declined') return { label: `Customer declined the ${docWord.toLowerCase()}` };
            if (d.response === 'changes_requested') return { label: 'Customer requested changes' };
            return { label: 'Customer responded' };
        }
        default: {
            const text = humanize(entry.type);
            return { label: text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Activity' };
        }
    }
}

// One <li>. noteIds/notesLoading are only consulted for note_added rows, so the
// follow-up-only surface can leave them at their defaults.
const QuoteActivityRow = ({
    entry,
    docWord,
    stages = null,
    noteIds = null,
    notesLoading = true,
    onOpenNotes,
    closeRecord = null,
}) => {
    const isNote = entry.type === T.NOTE_ADDED;
    // qclose-3e8a5c17 — the operator's answers live on the quoteCloses record
    // (entries never change once written), which describes the LATEST close
    // only. So answers are shown on that one row; an older close shows none.
    const closeAnswers = entry.type === T.CLOSED && closeRecord && closeRecord.closeActivityId === entry.id
        ? closeRecord : null;
    const answerLine = closeAnswers
        ? [closeAnswers.reason, closeAnswers.priceFeedback].filter(Boolean).join(' · ')
        : '';
    const noteState = isNote && !notesLoading && noteIds && !noteIds.has(entry.data?.noteId) ? 'deleted' : 'present';
    const clickable = isNote && noteState === 'present' && typeof onOpenNotes === 'function';
    const { label, sublabel } = describe(entry, docWord, noteState, stages);
    const who = entry.actor?.name
        ? `by ${entry.actor.name}`
        : entry.actor?.kind === 'system' ? 'automatic' : '';

    const body = (
        <>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className={`text-sm font-medium ${noteState === 'deleted' ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                    {label}
                    {sublabel && (
                        <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">{sublabel}</span>
                    )}
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap">{formatWhen(entry.at)}</span>
            </div>
            {who && <p className="text-xs text-gray-500 mt-0.5">{who}</p>}
            {entry.note && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{entry.note}</p>}
            {answerLine && <p className="text-sm text-gray-700 mt-1">{answerLine}</p>}
            {closeAnswers?.note && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{closeAnswers.note}</p>}
        </>
    );

    return (
        <li className="py-2.5">
            {clickable ? (
                <button
                    type="button"
                    onClick={onOpenNotes}
                    className="w-full text-left rounded hover:bg-gray-50 -mx-1 px-1"
                    title="Open Notes & Media"
                >
                    {body}
                </button>
            ) : body}
        </li>
    );
};

export default QuoteActivityRow;
